import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Brain, Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const CATS = ["general", "family", "goals", "coping", "triggers", "relationships", "achievements", "preferences", "important_dates"];

export default function Memories() {
  const { t } = useI18n();
  const [memories, setMemories] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("general");

  const load = () => api.get("/memories").then(({ data }) => { setMemories(data.memories || []); setEnabled(data.memory_enabled); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!content.trim()) return;
    try { await api.post("/memories", { content, category }); setContent(""); load(); toast.success("Memory saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const hide = async (id) => { await api.post(`/memories/${id}/hide`); load(); };
  const del = async (id) => { await api.delete(`/memories/${id}`); load(); };
  const toggle = async (v) => { const { data } = await api.post("/memories/toggle", { enabled: v }); setEnabled(data.memory_enabled); toast.success(v ? "Memory enabled" : "Memory paused"); };

  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("memories.title")}</h1>
      <p className="mt-2 max-w-lg text-muted-foreground">{t("memories.subtitle")}</p>

      <div className="mt-6 flex items-center justify-between rounded-3xl border border-border/60 bg-card p-5 ambient-shadow">
        <div>
          <p className="font-medium">{t("memories.ltm")}</p>
          <p className="text-sm text-muted-foreground">{t("memories.ltmDesc")}</p>
        </div>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="memory-toggle" />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Input value={content} onChange={(e) => setContent(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
               placeholder={t("memories.addPh")} data-testid="memory-input" className="min-w-64 flex-1 rounded-full" />
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-40 rounded-full capitalize" data-testid="memory-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace("_", " ")}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={add} className="rounded-full gap-1" data-testid="add-memory-button"><Plus className="h-4 w-4" strokeWidth={1.5} /> {t("memories.add")}</Button>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2">
        {memories.length === 0 && <p className="text-sm text-muted-foreground">{t("memories.empty")}</p>}
        {memories.map((m, i) => (
          <motion.div key={m.memory_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.04 }}
                      className={`ambient-shadow group rounded-3xl border border-border/60 bg-card p-5 ${m.hidden ? "opacity-50" : ""}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <Brain className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" strokeWidth={1.5} />
                <div>
                  <p className="text-sm">{m.content}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant="secondary" className="rounded-full text-xs capitalize">{m.category?.replace("_", " ")}</Badge>
                    <span className="text-xs text-muted-foreground">{m.source}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-shrink-0 gap-1 opacity-0 group-hover:opacity-100">
                <button onClick={() => hide(m.memory_id)} className="text-muted-foreground" data-testid={`hide-memory-${m.memory_id}`}>{m.hidden ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}</button>
                <button onClick={() => del(m.memory_id)} className="text-muted-foreground" data-testid={`delete-memory-${m.memory_id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
